/**
 * QHS loading skeletons.
 *
 * The product shows the shape of what is loading instead of a spinner in
 * an empty area: the reader sees where things will be, and nothing jumps
 * when the content lands.
 *
 * The rule: render the page ITSELF while it loads — titles, rails,
 * section headings, labels, help text are not data and appear at once —
 * and put a skeleton only where data goes, at that data's size. A
 * stand-in layout that is swapped for the real one makes the page jump;
 * the real layout with blank values does not. Forms do this with
 * `SkeletonSlot` / `SkeletonFields` over blank values; read-only values
 * swap their text for a `SkeletonBar`. Every piece uses the table rows' shimmer
 * (`.qhs-shimmer`), so every loading state looks alike.
 *
 *   <SkeletonBar width={120} height={18} />
 *   <ContentSkeleton />                       // a page whose shape is not known yet
 *   <SkeletonFields>{realForm}</SkeletonFields> // the real form, controls pulsing
 *
 * Mark the region busy for assistive tech; the bars themselves are
 * decoration.
 */
import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import cx from 'classnames';
import './SkeletonFields.css';

// Corner per shape. `text` is a line of type, drawn at 80% of its line
// box and centred in it, so a bar of `height={20}` takes the same room as
// a 20px line of text and sits where the text will.
const RADIUS = { text: 4, rounded: 'var(--qhs-r-md)', circular: '50%' };
// 80%: as full as the table rows' shimmer bars, which read as solid
// text rather than hairlines.
const TEXT_FILL = 0.8;

/**
 * One placeholder bar. Drawn with the shared `.qhs-shimmer` utility
 * (tokens.css) — the table rows' shimmer: `--qhs-skeleton` with a
 * `--qhs-skeleton-hi` highlight sweeping left to right, both swapped by
 * the dark theme — so every loading state in the product looks alike.
 *
 *   <SkeletonBar width={120} height={18} />                  // a line of text
 *   <SkeletonBar variant="rounded" width={90} height={36} /> // a button or input
 *   <SkeletonBar variant="circular" width={40} height={40} /> // an avatar
 *   <SkeletonBar variant="rounded" radius={999} ... />        // a pill
 */
export const SkeletonBar = ({ variant = 'text', width, height, radius, className, style }) => {
  const isText = variant === 'text' && typeof height === 'number';
  const barHeight = isText ? Math.round(height * TEXT_FILL) : height;
  const margin = isText ? (height - barHeight) / 2 : 0;
  return (
    <span
      aria-hidden="true"
      className={cx('qhs-shimmer', className)}
      style={{
        display: 'block',
        width,
        height: barHeight,
        margin: `${margin}px 0`,
        borderRadius: radius ?? RADIUS[variant],
        flexShrink: 0,
        ...style,
      }}
    />
  );
};

/**
 * True once `ms` has passed. A skeleton for a load that is usually
 * instant should wait this long, so a fast load shows nothing at all
 * rather than a flash of placeholders.
 */
export const useShowAfter = (ms = 300) => {
  const [show, setShow] = useState(ms <= 0);
  useEffect(() => {
    if (ms <= 0) return undefined;
    const timerId = setTimeout(() => setShow(true), ms);
    return () => clearTimeout(timerId);
  }, [ms]);
  return show;
};

const Busy = styled('div')({ display: 'flex', flexDirection: 'column', minHeight: 0 });

// --- Unknown page -------------------------------------------------------

// Most pages are lists, so a page whose shape is not known yet is drawn
// as one: a title row with its toolbar, then rows.
const TitleRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '20px 32px 12px',
});

const Spacer = styled('span')({ flex: 1 });

const Rows = styled('div')({ padding: '8px 32px' });

const ContentRoot = styled(Busy)({ height: '100%', width: '100%' });

const Row = styled('div')({
  display: 'flex',
  gap: 24,
  padding: '12px 0',
  borderBottom: '1px solid var(--qhs-border)',
});

const ROW_WIDTHS = [
  ['22%', '30%', '14%', '18%'],
  ['18%', '24%', '16%', '20%'],
  ['26%', '20%', '12%', '16%'],
];

/**
 * A page that is still being fetched or set up, before anything tells
 * us its shape. Waits `delay` ms first: route chunks and settings are
 * usually already there, and those loads should show nothing.
 */
export const ContentSkeleton = ({ rows = 8, delay = 300 }) => {
  const show = useShowAfter(delay);
  if (!show) return null;
  return (
    <ContentRoot aria-busy="true">
      <TitleRow>
        <SkeletonBar variant="text" width={180} height={30} />
        <SkeletonBar variant="rounded" width={56} height={22} radius={999} />
        <Spacer />
        <SkeletonBar variant="rounded" width={150} height={36} />
        <SkeletonBar variant="rounded" width={140} height={36} />
      </TitleRow>
      <Rows>
        {Array.from({ length: rows }, (_, index) => (
          <Row key={index}>
            {ROW_WIDTHS[index % ROW_WIDTHS.length].map((width, cell) => (
              <SkeletonBar key={cell} variant="text" width={width} height={18} />
            ))}
          </Row>
        ))}
      </Rows>
    </ContentRoot>
  );
};

/**
 * Wraps the real form while its record loads. Headings, labels and help
 * text render as themselves; every control keeps its exact box and
 * pulses with its contents hidden (SkeletonFields.css). When the values
 * arrive the wrapper goes and the controls fill in where they stand —
 * the best kind of skeleton is the page itself.
 *
 * Inert while it shows: nothing inside can be clicked or focused.
 */
export const SkeletonFields = ({ children }) => (
  <div className="qhs-skeleton-fields" aria-busy="true" inert="">
    {children}
  </div>
);

/** `SkeletonFields` around the children while `active`, the children alone otherwise. */
export const SkeletonSlot = ({ active, children }) =>
  active ? <SkeletonFields>{children}</SkeletonFields> : children;
